/**
 * Signup Card Component
 *
 * Inline signup form within the chat ("Kostenlos registrieren").
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { usePublicAuth } from '../hooks/use-public-auth';
import { readAttributionFromBrowser } from 'features/engagement/signup/attribution';

/** Same legal documents the dashboard sign-up links to. */
const AGB_URL = '/dashboard/legal/agb.html';
const PRIVACY_URL = 'https://secret-ai-agent.de/datenschutz';
const DPA_URL = '/dashboard/legal/avv.html';

/** The API accepts 6–50 characters; the form asks for 8 so accounts start with a sturdier password. */
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 50;
/** Matches the API's InlineSignupSchema. */
const COMPANY_MAX_LENGTH = 30;

interface SignupCardProps {
  sessionId: string;
  onSwitchToLogin: () => void;
  onClose?: () => void;
}

type FormField = 'firstName' | 'lastName' | 'email' | 'password' | 'passwordRepeat' | 'companyName';

export const SignupCard: React.FC<SignupCardProps> = ({ sessionId, onSwitchToLogin, onClose }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { signup, isLoading, error, clearError } = usePublicAuth();

  const [formData, setFormData] = useState<Record<FormField, string>>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordRepeat: '',
    companyName: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Optional consents (Engagement Center). Never pre-ticked — § 7 UWG needs an active opt-in.
  const [consentMarketingEmail, setConsentMarketingEmail] = useState(false);
  const [consentAdMeasurement, setConsentAdMeasurement] = useState(false);
  const [attribution] = useState(() => readAttributionFromBrowser(window.location.search));
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleChange = (field: FormField) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    clearError();
  };

  // Live feedback for the repeat field: only once something was typed there.
  const repeatTouched = formData.passwordRepeat.length > 0;
  const passwordsMatch = repeatTouched && formData.password === formData.passwordRepeat;
  const passwordLongEnough = formData.password.length >= PASSWORD_MIN_LENGTH;
  // Red only once the repeat is at least as long as the password — not while still typing.
  const repeatMismatch = repeatTouched && !passwordsMatch && formData.passwordRepeat.length >= formData.password.length;

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = t('publicChat.errorFirstName', 'Bitte gib deinen Vornamen ein.');
    }
    if (!formData.lastName.trim()) {
      errors.lastName = t('publicChat.errorLastName', 'Bitte gib deinen Nachnamen ein.');
    }
    if (!formData.email.trim()) {
      errors.email = t('publicChat.errorEmailRequired', 'Bitte gib deine E-Mail-Adresse ein.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = t('publicChat.errorEmailInvalid', 'Bitte gib eine gültige E-Mail-Adresse ein.');
    }
    if (!formData.password) {
      errors.password = t('publicChat.errorPasswordRequired', 'Bitte wähle ein Passwort.');
    } else if (formData.password.length < PASSWORD_MIN_LENGTH) {
      errors.password = t('publicChat.errorPasswordShort', 'Das Passwort braucht mindestens {{count}} Zeichen.', {
        count: PASSWORD_MIN_LENGTH,
      });
    } else if (formData.password.length > PASSWORD_MAX_LENGTH) {
      errors.password = t('publicChat.errorPasswordLong', 'Das Passwort darf höchstens {{count}} Zeichen haben.', {
        count: PASSWORD_MAX_LENGTH,
      });
    }
    if (!formData.passwordRepeat) {
      errors.passwordRepeat = t('publicChat.errorPasswordRepeatRequired', 'Bitte gib das Passwort noch einmal ein.');
    } else if (formData.passwordRepeat !== formData.password) {
      errors.passwordRepeat = t('publicChat.errorPasswordMismatch', 'Die Passwörter stimmen nicht überein.');
    }
    if (!termsAccepted) {
      errors.terms = t('publicChat.errorTerms', 'Bitte stimme den AGB und der Datenschutzerklärung zu.');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // passwordRepeat is only for the user's own check — it is not sent.
    await signup(
      {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        companyName: formData.companyName.trim(),
        acceptedLegalTerms: true,
        attribution,
        consentMarketingEmail,
        consentAdMeasurement,
      },
      sessionId
    );
  };

  const fieldIcon = (icon: React.ReactNode) => (
    <InputAdornment position="start" className={classes.fieldIcon}>
      {icon}
    </InputAdornment>
  );

  const visibilityToggle = (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setShowPassword((prev) => !prev)}
        edge="end"
        size="small"
        aria-label={showPassword ? t('publicChat.hidePassword', 'Passwort verbergen') : t('publicChat.showPassword', 'Passwort anzeigen')}
      >
        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box className={classes.card}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography variant="h6" className={classes.title}>
            {t('publicChat.signupTitle', 'Erstelle dein Konto')}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {t('publicChat.signupSubtitle', 'Starte deine kostenlose 14-Tage-Testversion')}
          </Typography>
        </Box>
        {onClose && (
          <IconButton onClick={onClose} size="small" className={classes.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {error && (
        <Alert severity="error" className={classes.alert}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className={classes.form} noValidate>
        {/* Persönliche Angaben */}
        <Typography className={classes.sectionLabel}>{t('publicChat.sectionPerson', 'Über dich')}</Typography>
        <Box className={classes.nameRow}>
          <TextField
            label={t('publicChat.firstName', 'Vorname')}
            value={formData.firstName}
            onChange={handleChange('firstName')}
            error={!!validationErrors.firstName}
            helperText={validationErrors.firstName}
            size="small"
            fullWidth
            autoComplete="given-name"
            InputProps={{ startAdornment: fieldIcon(<PersonOutlineIcon fontSize="small" />) }}
          />
          <TextField
            label={t('publicChat.lastName', 'Nachname')}
            value={formData.lastName}
            onChange={handleChange('lastName')}
            error={!!validationErrors.lastName}
            helperText={validationErrors.lastName}
            size="small"
            fullWidth
            autoComplete="family-name"
          />
        </Box>
        <TextField
          label={t('publicChat.email', 'E-Mail')}
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          size="small"
          fullWidth
          autoComplete="email"
          InputProps={{ startAdornment: fieldIcon(<MailOutlineIcon fontSize="small" />) }}
        />
        <TextField
          label={t('publicChat.companyName', 'Firmenname (optional)')}
          value={formData.companyName}
          onChange={handleChange('companyName')}
          size="small"
          fullWidth
          autoComplete="organization"
          inputProps={{ maxLength: COMPANY_MAX_LENGTH }}
          InputProps={{ startAdornment: fieldIcon(<BusinessOutlinedIcon fontSize="small" />) }}
        />

        {/* Passwort mit Wiederholung */}
        <Typography className={classes.sectionLabel}>{t('publicChat.sectionPassword', 'Passwort')}</Typography>
        <TextField
          label={t('publicChat.password', 'Passwort')}
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange('password')}
          error={!!validationErrors.password}
          helperText={
            validationErrors.password ||
            t('publicChat.passwordHint', 'Mindestens {{count}} Zeichen.', { count: PASSWORD_MIN_LENGTH })
          }
          FormHelperTextProps={{
            className: cx(!validationErrors.password && passwordLongEnough && classes.hintOk),
          }}
          size="small"
          fullWidth
          autoComplete="new-password"
          inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
          InputProps={{ startAdornment: fieldIcon(<LockOutlinedIcon fontSize="small" />), endAdornment: visibilityToggle }}
        />
        <TextField
          label={t('publicChat.passwordRepeat', 'Passwort wiederholen')}
          type={showPassword ? 'text' : 'password'}
          value={formData.passwordRepeat}
          onChange={handleChange('passwordRepeat')}
          error={!!validationErrors.passwordRepeat || repeatMismatch}
          helperText={
            validationErrors.passwordRepeat ||
            (repeatTouched ? (
              <span className={cx(classes.matchHint, passwordsMatch ? classes.hintOk : undefined)}>
                {passwordsMatch ? <CheckCircleIcon fontSize="inherit" /> : <ErrorOutlineIcon fontSize="inherit" />}
                {passwordsMatch
                  ? t('publicChat.passwordsMatch', 'Passwörter stimmen überein')
                  : t('publicChat.errorPasswordMismatch', 'Die Passwörter stimmen nicht überein.')}
              </span>
            ) : (
              t('publicChat.passwordRepeatHint', 'Zur Sicherheit noch einmal eingeben.')
            ))
          }
          size="small"
          fullWidth
          autoComplete="new-password"
          inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
          InputProps={{ startAdornment: fieldIcon(<LockOutlinedIcon fontSize="small" />), endAdornment: visibilityToggle }}
        />

        {/* Consent is mandatory — the button below stays disabled until it is given. */}
        <Box className={cx(classes.consentBox, validationErrors.terms && classes.consentBoxError)}>
          <FormControlLabel
            className={classes.termsControl}
            control={
              <Checkbox
                checked={termsAccepted}
                size="small"
                onChange={(event) => {
                  setTermsAccepted(event.target.checked);
                  setValidationErrors((prev) => ({ ...prev, terms: '' }));
                }}
              />
            }
            label={
              <Typography variant="caption" className={classes.termsLabel}>
                {t('acceptTermsAndPrivacy', 'Ich habe die AGB und die Datenschutzerklärung gelesen und stimme ihnen zu.')}
              </Typography>
            }
          />
          <Box className={classes.termsLinks}>
            <Link href={AGB_URL} target="_blank" rel="noopener" variant="caption" underline="hover">
              {t('termsOfUse', 'AGB')}
            </Link>
            <Link href={PRIVACY_URL} target="_blank" rel="noopener" variant="caption" underline="hover">
              {t('privacyPolicy', 'Datenschutzerklärung')}
            </Link>
            <Link href={DPA_URL} target="_blank" rel="noopener" variant="caption" underline="hover">
              {t('dataProcessingAgreement', 'Auftragsverarbeitung')}
            </Link>
          </Box>
        </Box>

        {/* TODO(legal): wording of both optional consents is pending legal review (same keys as the sign-up page). */}
        <FormControlLabel
          className={classes.termsControl}
          control={
            <Checkbox
              checked={consentMarketingEmail}
              size="small"
              onChange={(event) => setConsentMarketingEmail(event.target.checked)}
            />
          }
          label={<Typography variant="caption" className={classes.termsLabel}>{t('engSignupConsentMarketing')}</Typography>}
        />
        <FormControlLabel
          className={classes.termsControl}
          control={
            <Checkbox
              checked={consentAdMeasurement}
              size="small"
              onChange={(event) => setConsentAdMeasurement(event.target.checked)}
            />
          }
          label={<Typography variant="caption" className={classes.termsLabel}>{t('engSignupConsentAdMeasurement')}</Typography>}
        />

        <Button type="submit" variant="contained" fullWidth disabled={isLoading || !termsAccepted} className={classes.submitButton}>
          {isLoading ? <CircularProgress size={22} color="inherit" /> : t('publicChat.createAccount', 'Konto erstellen')}
        </Button>
        <Typography className={classes.trustLine}>
          {t('publicChat.signupTrustLine', '14 Tage kostenlos testen · jederzeit kündbar')}
        </Typography>
      </form>

      <Box className={classes.switchAuth}>
        <Typography variant="body2" className={classes.switchText}>
          {t('publicChat.alreadyHaveAccount', 'Bereits ein Konto?')}
        </Typography>
        <Button variant="text" size="small" onClick={onSwitchToLogin} className={classes.switchButton}>
          {t('publicChat.loginInstead', 'Anmelden')}
        </Button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.greys.lighter}`,
    padding: theme.spacing(3.5, 3.5, 3),
    maxWidth: 560,
    boxShadow: '0 8px 32px rgba(17, 12, 46, 0.08)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2.5),
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    letterSpacing: '-0.2px',
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  closeButton: {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  alert: {
    marginBottom: theme.spacing(2),
    borderRadius: 10,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.75),
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
    },
  },
  sectionLabel: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  nameRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  fieldIcon: {
    color: theme.palette.text.secondary,
  },
  hintOk: {
    color: `${theme.palette.success.main} !important`,
  },
  matchHint: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  consentBox: {
    borderRadius: 10,
    padding: theme.spacing(1, 1.5, 1.25),
    backgroundColor: theme.palette.backgrounds?.main_new ?? theme.palette.grey[50],
    border: '1px solid transparent',
  },
  consentBoxError: {
    borderColor: theme.palette.error.main,
  },
  termsControl: {
    alignItems: 'flex-start',
    margin: 0,
  },
  termsLabel: {
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    display: 'block',
    paddingTop: theme.spacing(0.75),
  },
  termsLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    paddingLeft: theme.spacing(3.75),
  },
  submitButton: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(15),
    padding: theme.spacing(1.4),
    borderRadius: 10,
    textTransform: 'none',
    marginTop: theme.spacing(0.5),
    boxShadow: 'none',
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
      boxShadow: 'none',
    },
  },
  trustLine: {
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  switchAuth: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
  },
  switchText: {
    color: theme.palette.text.secondary,
  },
  switchButton: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0.25, 0.5),
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}14`,
    },
  },
}));

export default SignupCard;
